/**
 * Purpose: These selectors utilize the "getOfferNode" helper function to work with the offer state in a more app-agnostic way to
 *   ensure that the offer state is retrieved correctly.  Any functions that work directly with the offer state (rather than the
 *   app state) should not be in this file.
 */

// externals
import isEmpty from 'lodash.isempty';

// libraries
import type { FeatureToggles, IDealSummary } from '@makemydeal/dr-platform-shared';
import { isPaymentInvalid } from '@makemydeal/dr-platform-shared';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

import type {
    CalculationEndpointType,
    CalculationEndpoints,
    CoBuyer,
    CreditDecisionDetails,
    CreditTierByIdDetails,
    DealerSource,
    ICreditDecisionLender,
    IOfferTradeIn,
    IPencilOfferInputs,
    IPenciledInputs,
    IShopper,
    IncompleteDealDetails,
    ItemizedDiscount,
    ManualIncentiveEntry,
    Offer,
    OfferIncentive,
    OfferIncentives,
    OfferSettings,
    OfferType,
    OutsideLienHolder,
    PartialRestoreRecord,
    PaymentSchedule,
    PaymentTerm,
    TermPaymentDetail,
    TitlingLender,
    Vehicle,
    paymentServicesTypes,
    DeferredDownPayment
} from '@makemydeal/dr-platform-types';
import {
    ApplicationCodes,
    BALLOON,
    CASH,
    FINANCE,
    LEASE,
    OFFER_ENCRYPTION_FIELDS,
    OFFER_TYPE,
    VEHICLE_ENCRYPTION_FIELDS,
    SpecialPaymentFormula
} from '@makemydeal/dr-platform-types';
import { reducerConfig } from '../reducers/reducerConfig';
import { getAutoSelectProgramByDefault } from './dealerSelectors';

// interfaces/types
import type {
    CreditTierNameAndScore,
    CreditTierScoreOnly,
    DealXgDetails,
    ExtendedFeatureToggles,
    GetCashOfferAdditionalSelectors,
    OfferTermInputs,
    PenciledInputsState,
    PriorLeaseBalance,
    PriorLeaseBalanceForPayment,
    TotalTaxesAndFees
} from '../types/commonTypes';
import { KEYID_DELIMETER } from '../types/commonTypes';

// utils
import { createSelector } from 'reselect';
import { CALC_ENDPOINTS } from '../constants/state';
import * as helpers from './helpers';
import {
    getDealerDiscounts,
    getDealerDiscountsTotalAmount,
    getInputPaymentFrequency,
    getOfferPenciledInputs,
    getOfferTypeTermId
} from './penciledInputsSelectors';
import { getAllTermsFromOffer } from './slices/offerSliceSelectors';
import { isOnePayLeaseFrequency } from './utils/nonSelectorUtils';

export const getOffer = (state: OfferReduxBaseState): Offer => {
    return helpers.getOfferNode(state);
};

export function getCurrentOfferType(state: OfferReduxBaseState): OfferType {
    const offerType = getOffer(state).offerType;
    return offerType as OfferType;
}

export function getFeatureToggles(state: OfferReduxBaseState): FeatureToggles {
    return getOffer(state).featureToggles || {};
}

export function getFeatureToggle(state: OfferReduxBaseState, featureToggle: keyof ExtendedFeatureToggles): boolean {
    return (getFeatureToggles(state) as any)[featureToggle] || false;
}

/**
 * Reads the toggle from `offer.featureToggles`, so it is safe to call with a bare `{ offer }` — which the
 * penciled-inputs reducer has to do, since a slice sub-reducer never receives the full state tree.
 * `paymentPayloadUtils` deliberately does not use this: it reads the global `state.featureToggles`, the only
 * slice that survives `withoutScenarioFeatureToggles` for non-primary scenarios.
 */
export function isAspPaymentGridRedesignEnabled(state: OfferReduxBaseState): boolean {
    return getFeatureToggle(state, 'F298789_ASP_PG_redesign');
}

export function isUpqResidualKeepOnMileageChangeEnabled(state: OfferReduxBaseState): boolean {
    return getFeatureToggle(state, 'DE783787_Enable_UPQ_Residual_Keep_On_Mileage_Change');
}

export function getWalletId(state: OfferReduxBaseState) {
    const walletId = getOffer(state).walletId || '';
    return walletId;
}

/** Offer types whose payment grid forces the calculation endpoint to program-quotes. */
const PROGRAM_QUOTES_GRID_OFFER_TYPES: readonly OfferType[] = [LEASE, BALLOON];

/** Mirrors dr-dash-store's vehicleSelectors.getHasVehicle */
const getHasVehicle = (state: OfferReduxBaseState): boolean => {
    const vehicle: Vehicle = getVehicleDetails(state);
    return Boolean(vehicle?.make || vehicle?.model || vehicle?.trim);
};

/** Lives here, not in paymentGridSelectors, so getPaymentGridToggle can use it without a circular import. */
export const shouldGatePaymentGridForQuote = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    if (!getFeatureToggle(state, 'FEAT278181_MV_Quick_Quote_Mode')) {
        return false;
    }

    const resolvedOfferType = offerType || getCurrentOfferType(state);

    if (!PROGRAM_QUOTES_GRID_OFFER_TYPES.includes(resolvedOfferType)) {
        return false;
    }

    return !getShopperInfo(state)?.lastName || !getHasVehicle(state);
};

/** Also covers a restored/persisted `paymentGridToggle: true` that should stay off while gated. */
export function getPaymentGridToggle(state: any, offerType: OfferType): boolean {
    const offerDetails = getOfferDetails(state, offerType);
    const isToggledOn = offerDetails.paymentGridData?.paymentGridToggle || false;

    return isToggledOn && !shouldGatePaymentGridForQuote(state, offerType);
}

export function isOfferUpdatedByUser(state: OfferReduxBaseState) {
    const offerDetails = getOfferDetails(state, getCurrentOfferType(state));
    return offerDetails.isUpdatedByUser;
}

export function getPenciledInputs(state: OfferReduxBaseState): IPenciledInputs {
    const offer: Offer = getOffer(state);
    return getOfferPenciledInputs(offer);
}

export function getOfferDetailsByOfferType(penciledInputs: IPenciledInputs, offerType: OfferType): IPencilOfferInputs {
    return penciledInputs.byOfferType?.[offerType] || {};
}

export function getOfferDetails(state: any, offerType?: OfferType): IPencilOfferInputs {
    const penciledInputs = getPenciledInputs(state);
    const targetOfferType = offerType || getCurrentOfferType(state);
    return getOfferDetailsByOfferType(penciledInputs, targetOfferType);
}

/**
 * Selector to retrieve penciled input details for a specific offer type.
 *
 * @param state - The application state.
 * @param offerType - (Optional) The type of offer to retrieve penciled inputs for.
 * @returns The penciled input details for the specified offer type.
 *
 * @remarks
 * Use the `getOfferTypePenciledInputs` selector to access penciled input details from the state.
 */
export function getOfferTypePenciledInputs(state: any, offerType?: OfferType): IPencilOfferInputs {
    return getOfferDetails(state, offerType);
}

export function getDealXgDetails(state: OfferReduxBaseState): DealXgDetails {
    const offer: Offer = getOffer(state);
    return offer.dealXgDetails ?? ({} as DealXgDetails);
}

export function getCommonOrgId(state: OfferReduxBaseState) {
    const offer = getOffer(state);
    return offer.commonOrgId || '';
}

export function getDealerSettings(state: OfferReduxBaseState): OfferSettings {
    const offer = getOffer(state);
    return offer.settings;
}

export function getVehicleDetails(state: OfferReduxBaseState): Vehicle {
    const offer = getOffer(state);
    return offer.vehicle;
}

export function getTerm(state: OfferReduxBaseState, termId: string | undefined): PaymentTerm {
    const offer = getOffer(state);
    if (!termId) return {} as PaymentTerm;
    return offer?.terms?.byId?.[termId] || ({} as PaymentTerm);
}

/** Canonical key for a lease term record: (months, requestedDownPayment). */
export const leaseRecordKey = (months: number, requestedDownPayment: number): string => `${months}|${requestedDownPayment}`;

/**
 * Index of lease term records keyed by (months, requestedDownPayment) — the single source of truth for
 * lease-record lookup. The key uses `requestedDownPayment` (the raw axis value), which is stable even
 * when Payment Service floors a sub-minimum request to the lender minimum and re-keys the stored term
 * id to that resolved cash-down. Callers apply their own usability filter (payment present, residuals
 * present, coverage). Takes the offer (not state) so memoized selectors can pass their offer input.
 */
export const getLeaseRecordsByTermAndDownPayment = (offer: Offer): Map<string, PaymentTerm> => {
    const recordsByKey = new Map<string, PaymentTerm>();
    const leaseTermIds: string[] = offer?.terms?.leaseTerms ?? [];
    for (const termId of leaseTermIds) {
        const record = offer?.terms?.byId?.[termId];
        const requestedDownPayment = (record as { requestedDownPayment?: number })?.requestedDownPayment;
        if (record && record.months != null && requestedDownPayment != null) {
            recordsByKey.set(leaseRecordKey(record.months, requestedDownPayment), record);
        }
    }
    return recordsByKey;
};

export function getAnnualMilesOptions(state: OfferReduxBaseState) {
    const offer = getOffer(state);
    return helpers.getMileageOptions(offer.leaseAnnualMiles);
}

export function isLeasingEnabled(state: OfferReduxBaseState): boolean {
    const offer = getOffer(state);
    return offer.settings.isLeaseEnabled && offer.terms.leaseTerms!.length > 0;
}

export function shouldDisplayCash(state: OfferReduxBaseState): boolean {
    const offer = getOffer(state);
    return offer.settings.isCashEnabled;
}

export function getAvailableFinanceTermsOptions(state: OfferReduxBaseState) {
    const offer = getOffer(state);
    const financeTermsIds = offer.terms.financeTerms;

    return financeTermsIds!.map((termId) => {
        return helpers.formatOptionsForFinanceTerms(offer.terms.byId![termId]);
    });
}

export function getAvailableLeaseTermsOptions(state: OfferReduxBaseState) {
    const offer = getOffer(state);
    const leaseTermsIds = offer.terms.leaseTerms;

    return leaseTermsIds!.map((termId) => {
        return helpers.formatOptionsForLeaseTerms(offer.terms.byId![termId]);
    });
}

export function getLeaseTerms(state: OfferReduxBaseState) {
    const leaseTerms: any = {};
    const offer = getOffer(state);
    const leaseTermsIds = offer.terms.leaseTerms;

    leaseTermsIds!.map((termId) => {
        leaseTerms[offer.terms.byId![termId].months] = offer.terms.byId![termId];
    });
    return leaseTerms;
}

export function getCreditTierById(state: OfferReduxBaseState, crediTierId: any): CreditTierNameAndScore {
    // crediTierId is an object
    const offer = getOffer(state);
    return offer.creditTiers?.byId[crediTierId] || ({} as CreditTierNameAndScore);
}

export function getCreditTierByIdOrNull(state: any, crediTierId: any): CreditTierNameAndScore | null | undefined {
    if (!crediTierId) {
        return null;
    }
    const offer = getOffer(state);
    if (!offer) {
        return null;
    }
    return offer.creditTiers?.byId?.[crediTierId]; // byId ins an empty object
}

export function getDealerDefaultCreditTier(state: OfferReduxBaseState): CreditTierNameAndScore | undefined {
    const byId = getOffer(state).creditTiers?.byId;
    return byId && Object.values(byId).find((tier) => tier.isDefault === true);
}

export function getCreditTiers(state: OfferReduxBaseState): CreditTierByIdDetails[] {
    const offer = getOffer(state);
    const offerType = getCurrentOfferType(state);
    const creditTiersIdsForOfferType = offerType === OFFER_TYPE.finance ? offer.creditTiers?.byFinance : offer.creditTiers?.byLease;

    const creditTierCollectionsForOffer = creditTiersIdsForOfferType!.map((creditTierId) => {
        return offer.creditTiers?.byId[creditTierId];
    });

    return helpers.getCreditTierRanges(creditTierCollectionsForOffer);
}

export function getFinanceCreditTiers(state: OfferReduxBaseState): CreditTierByIdDetails[] {
    const offer = getOffer(state);
    if (!offer.creditTiers || !offer.creditTiers?.byFinance) return [] as CreditTierByIdDetails[];

    const creditTiersIdsForFinanceType = offer.creditTiers.byFinance;
    const creditTierCollectionById = creditTiersIdsForFinanceType.map(
        (creditTierId: string) => offer.creditTiers!.byId[creditTierId]
    );

    return creditTierCollectionById;
}

export function getLeaseCreditTiers(state: OfferReduxBaseState): CreditTierByIdDetails[] {
    const offer = getOffer(state);
    if (!offer.creditTiers || !offer.creditTiers?.byLease) return [] as CreditTierByIdDetails[];
    const creditTiersIdsForLeaseType = offer.creditTiers?.byLease;
    const creditTierCollectionById = creditTiersIdsForLeaseType!.map((creditTierId) => offer.creditTiers!.byId[creditTierId]);

    return creditTierCollectionById;
}

export function getTermByOfferTypeAndMonths(
    state: OfferReduxBaseState,
    offerType: OfferType,
    months: number
): PaymentTerm | undefined {
    const offer = getOffer(state);
    let termsForOffer: 'financeTerms' | 'leaseTerms' | 'balloonTerms';
    if (offerType === OFFER_TYPE.finance) {
        termsForOffer = 'financeTerms';
    } else if (offerType === OFFER_TYPE.lease) {
        termsForOffer = 'leaseTerms';
    } else {
        termsForOffer = 'balloonTerms';
    }

    const termIds = offer.terms[termsForOffer];
    if (!termIds?.length) return undefined;
    for (let ix = 0; ix < termIds.length; ix++) {
        // simple loop; breaks when found; not using callback
        const termId = termIds[ix];
        const term = offer.terms.byId![termId];
        if (term?.months === months) return term;
    }
    return undefined;
}

export function getTermsByOfferType(state: any, offerType: any) {
    const offer = getOffer(state);
    const termsForOffer = offerType === OFFER_TYPE.finance ? 'financeTerms' : 'leaseTerms';

    return offer.terms[termsForOffer]?.map((termId) => {
        return {
            months: offer.terms.byId![termId].months
        };
    });
}

export function getOfferSettings(state: OfferReduxBaseState): OfferSettings {
    const offer = getOffer(state);
    return offer.settings;
}

export function getShopperInfo(state: OfferReduxBaseState): IShopper {
    const offer = getOffer(state);
    return offer.shopper;
}

export const getCoBuyerInfo = (state: OfferReduxBaseState): CoBuyer | undefined => {
    return getOffer(state).coBuyer;
};

export function getMessage(state: OfferReduxBaseState): string | undefined {
    const offer: Offer = getOffer(state);
    return offer?.message;
}

export const getIncentives = (state: OfferReduxBaseState): OfferIncentives => {
    const offer = getOffer(state);
    return offer.incentives || ({} as OfferIncentives);
};

export const getIncentiveById = (state: OfferReduxBaseState, id: string): OfferIncentive => {
    const incentives = getIncentives(state);
    return incentives.byId?.[id] || ({} as OfferIncentive);
};

const isNegativeTradeInFixOnGlobally = (state: OfferReduxBaseState): boolean =>
    Boolean(state.featureToggles?.DE770955_Enable_Negative_Trade_In_Fix);

const isMultiTradeInsOn = (state: OfferReduxBaseState): boolean =>
    getFeatureToggle(state, 'enableMultiTradeIns') ||
    (isNegativeTradeInFixOnGlobally(state) && Boolean((state as any).featureToggles?.enableMultiTradeIns));

const getMultiTradeInRows = (state: OfferReduxBaseState): any[] | undefined => {
    const multiTradeIns = state.multiTradeIns?.tradeIns;
    return isMultiTradeInsOn(state) && multiTradeIns?.length > 0 ? multiTradeIns : undefined;
};

const isTradeInRowIncluded = (row: any): boolean =>
    (row?.tradeInComponent?.shouldIncludeTradeIn ?? row?.tradeIn?.shouldIncludeTradeIn) !== false;

const getRowsToCount = (state: OfferReduxBaseState, rows: any[]): any[] =>
    isNegativeTradeInFixOnGlobally(state) ? rows.filter(isTradeInRowIncluded) : rows;

const getRowValue = (row: any): number => row?.tradeIn?.value || 0;

const getRowAmountOwed = (row: any): number => row?.tradeIn?.amountOwed || 0;

const getLegacyRowAmountOwed = (row: any): number => (getRowValue(row) !== 0 ? getRowAmountOwed(row) : 0);

const sumRows = (rows: any[], getAmount: (row: any) => number): number =>
    rows.reduce((total: number, row: any) => total + getAmount(row), 0);

/**
 * Helper function to calculate total value from multiple trade-ins.
 * enableMultiTradeIns is specific to SV; MV will follow in a later sprint
 * (https://coxauto.slack.com/archives/C0A75EFQL2F/p1773155182888709).
 * With DE770955_Enable_Negative_Trade_In_Fix on, rows excluded from the deal are not counted.
 * @param state - Redux state
 * @param fallbackValue - Value to return when multi-trade-ins is not enabled or empty
 * @returns Sum of all trade-in values or fallback value
 */
export const getTradeInsValue = (state: OfferReduxBaseState, fallbackValue: number): number => {
    const rows = getMultiTradeInRows(state);
    return rows ? sumRows(getRowsToCount(state, rows), getRowValue) : fallbackValue;
};

/**
 * Helper function to calculate total amount owed from trade-ins.
 * With DE770955_Enable_Negative_Trade_In_Fix on, a row's real payoff is counted even when its value is 0
 * (negative equity) and rows excluded from the deal are not counted, matching the payment request.
 * With it off, a row whose value is 0 contributes no amount owed.
 * @param state - Redux state
 * @param fallbackValue - Value to return when multi-trade-ins is not enabled or empty
 * @returns Sum of amounts owed or fallback value
 */
export const getTradeInsAmountOwed = (state: OfferReduxBaseState, fallbackValue: number): number => {
    const rows = getMultiTradeInRows(state);
    if (!rows) {
        return fallbackValue;
    }
    return isNegativeTradeInFixOnGlobally(state)
        ? sumRows(getRowsToCount(state, rows), getRowAmountOwed)
        : sumRows(rows, getLegacyRowAmountOwed);
};

export function getTradeInValue(state: OfferReduxBaseState): number {
    const offer: Offer = getOffer(state);
    // finance and lease have the same trade-in
    const tradeIn = offer?.penciledInputs?.byOfferType?.finance?.tradeIn;
    const fallbackValue = tradeIn?.value || 0;
    return getTradeInsValue(state, fallbackValue);
}

export function getTradeInAmountOwed(state: OfferReduxBaseState): number {
    const offer = getOffer(state);
    const tradeIn = offer?.penciledInputs?.byOfferType?.finance?.tradeIn;
    const fallbackValue = tradeIn?.amountOwed || 0;
    return getTradeInsAmountOwed(state, fallbackValue);
}

export function shouldIncludeTradeIn(state: OfferReduxBaseState): boolean {
    const offer: Offer = getOffer(state);
    const tradeIn: IOfferTradeIn | undefined = offer?.penciledInputs?.byOfferType?.finance?.tradeIn;
    return tradeIn?.shouldIncludeTradeIn || false;
}

export function getOfferCreationDateString(state: OfferReduxBaseState): string {
    const offerCreationDate = getOffer(state).createdOn || '';
    return offerCreationDate;
}

export function getOfferCreationDate(state: OfferReduxBaseState): Date | undefined {
    const dateString = getOfferCreationDateString(state);
    if (!dateString) {
        return undefined;
    }
    return new Date(dateString);
}

export const hasDealBeenAccepted = (state: OfferReduxBaseState): boolean => {
    return getOffer(state).accepted ?? false;
};

export const getCreditDecisionDetails = (state: OfferReduxBaseState): CreditDecisionDetails => {
    const offer = getOffer(state);
    return offer.creditApp?.decisionDetails ?? ({} as CreditDecisionDetails);
};

export const getShouldApplyDecisionDetails = (state: OfferReduxBaseState): boolean => {
    const offer = getOffer(state);
    return offer.creditApp?.shouldApplyDecisionDetails ?? false;
};

export function getOfferDealerSource(state: OfferReduxBaseState): DealerSource {
    const offer = getOffer(state);
    return offer.source;
}
export function getOfferSourceSponsor(state: OfferReduxBaseState): string {
    const source = getOfferDealerSource(state);
    return source.sponsor || '';
}

export const getIncompleteDealDetails = (state: OfferReduxBaseState): IncompleteDealDetails | undefined => {
    const offer = getOffer(state);
    return offer?.incompleteDealDetails;
};

export const getCalculationEndpointsState = (
    state: OfferReduxBaseState
): (CalculationEndpoints & { autoSelectProgramSavedAt?: string | null }) | undefined => {
    const offer = getOffer(state);
    return offer.calculationEndpoints;
};

/**
 * Live US1972679 rule: MV + admin toggle on + no marker + dealer opted out of `autoSelectProgramByDefault`.
 *
 * Reads all 3 inputs from current state on every call so the PQ/UPQ checkbox and PO calls react to
 * mid-session changes (e.g., dealer setting refreshed via `INIT_PENCIL_SUCCESS`, marker arriving via
 * `OFFER_SUBMISSION_SUCCESS`).
 */
/**
 * The dealer's standing opt-out, with no consent marker in it — deliberately not the same question as
 * `getDealerForcedUpq`. The marker is stamped on **every** MV save (`addMvAutoSelectProgramSavedBaggage`
 * is called unconditionally), so a marker-sensitive predicate stops describing the dealer's preference
 * the moment a deal is saved once. Use this one to decide whether the app may pick a program on the
 * dealer's behalf; use `getDealerForcedUpq` for the US1972679 endpoint gate, which is marker-sensitive
 * by design.
 */
export const getDealerOptedOutOfAutoSelectProgram = (state: OfferReduxBaseState): boolean => {
    const sourceApplication = reducerConfig?.sourceApplication;
    if (sourceApplication !== ApplicationCodes.MV) return false;
    const enableAutoSelectProgramAdminSetting = (getFeatureToggles(state) as ExtendedFeatureToggles)
        ?.enableAutoSelectProgramAdminSetting;
    if (enableAutoSelectProgramAdminSetting !== true) return false;
    return getAutoSelectProgramByDefault(state) === false;
};

export const getDealerForcedUpq = (state: OfferReduxBaseState): boolean => {
    if (getCalculationEndpointsState(state)?.autoSelectProgramSavedAt) return false;
    return getDealerOptedOutOfAutoSelectProgram(state);
};

export const getCustomRateGroup = (state: OfferReduxBaseState) => {
    const offer = getOffer(state);
    return offer.customRateGroup;
};

export function getFinanceTermAPR(state: OfferReduxBaseState): number | null {
    const selectedOfferDetails = getOfferDetails(state, FINANCE);
    const term = selectedOfferDetails.term;
    const offer = getOffer(state);
    const apr = getAllTermsFromOffer(offer)[term!] && getAllTermsFromOffer(offer)[term!].apr;
    if (apr === undefined) {
        return null;
    }
    return apr;
}

export const getCreditDecisionEnabledByOfferType = (state: OfferReduxBaseState, offerType: OfferType): boolean | undefined => {
    const inputs = getOfferDetails(state, offerType);
    return inputs.creditDecisionEnabled ?? false;
};

export function getSelectedCreditTierScore(state: OfferReduxBaseState): CreditTierScoreOnly {
    const offerDetails = getOfferDetails(state, getCurrentOfferType(state));
    const selectedCreditTierId = offerDetails.creditTier;

    const creditTier = getCreditTierById(state, selectedCreditTierId);
    const creditTierRange = creditTier && !isEmpty(creditTier) ? creditTier.score : { high: 0, low: 0 };

    return { score: creditTierRange };
}

export function getTotalTaxesAndFees(state: any, offerType: OfferType): TotalTaxesAndFees {
    const { totalTax = 0, dealerFeesTotal = 0, dmvFees = 0 } = getOfferDetails(state, offerType);
    const taxesAndFeesTotal = Math.ceil(totalTax + dealerFeesTotal + dmvFees) || 0;
    return {
        taxesAndFeesTotal,
        totalTax
    };
}

// For cash deals, returns the final cash amount the customer owes including all adds and less all credits
export function getCashTotalAmountDueAfterCredits(state: OfferReduxBaseState): number | undefined {
    const offerDetails = getOfferDetails(state, CASH);
    return offerDetails.totalAmountDueAfterCredits;
}

export function getCashTotalAmountDueWithoutProducts(state: any): number | undefined {
    const offerDetails = getOfferDetails(state, CASH);
    return offerDetails.totalAmountDueWithoutProducts;
}

export const getTermIdForOfferType = (offer: Offer, offerType: OfferType): string | undefined => {
    return getOfferDetails(offer, offerType).term;
};

/**
 * @returns adjusted residual overrides for the currently selected term OR default / undefined values
 */
export const getOfferDetailsMonth = (state: any, offerType: OfferType): number | undefined => {
    const termId = getTermIdForOfferType(state, offerType);
    return termId ? Number(termId.split(KEYID_DELIMETER)[2]) : undefined;
};

export function getOfferPrice(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, getCurrentOfferType(state));
    return selectedOfferDetails.offerPrice;
}

export function getInitialOfferPrice(state: OfferReduxBaseState): number {
    const selectedOfferDetails = getOfferDetails(state, getCurrentOfferType(state));
    return selectedOfferDetails.initialOfferPrice || 0;
}

export function getOfferPriceByOfferType(state: any, offerType: any): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, offerType);
    return selectedOfferDetails.offerPrice;
}

export const getCashOfferDetails = (state: OfferReduxBaseState): IPencilOfferInputs => getOfferDetails(state, CASH);

export const getNetTradeValue = (state: any): number => {
    if (isMultiTradeInsOn(state)) {
        const totalValue = getTradeInValue(state);
        const totalAmountOwed = getTradeInAmountOwed(state);
        return totalValue - totalAmountOwed;
    }

    const { tradeIn } = state;
    const value = tradeIn.value ? tradeIn.value : 0;
    const amountOwed = tradeIn.amountOwed ? tradeIn.amountOwed : 0;

    return Number(value) - Number(amountOwed);
};

export const getIncludedNetTradeValue = (
    state: any,
    additionalSelectors?: Pick<GetCashOfferAdditionalSelectors, 'getIsTradeIncluded'>
): number => {
    const netTrade = getNetTradeValue(state);
    const isTradeIncluded = additionalSelectors?.getIsTradeIncluded(state) ?? true;

    return isTradeIncluded ? netTrade : 0;
};

/**
 * @deprecated Use `getCashTotalAmountDueAfterCredits` instead.
 */
export function getCashOfferRoundedUp(
    state: any,
    additionalSelectors?: GetCashOfferAdditionalSelectors,
    showDecimal?: boolean,
    offerType?: OfferType
): number {
    const { offerPrice, totalTax = 0, dealerFeesTotal = 0, dmvFees = 0 } = getCashOfferDetails(state);
    const totalVppSelectionPrice = additionalSelectors?.getTotalVppSelectionPrice(state, offerType) ?? 0;
    const totalAccessoriesSelectionPrice = additionalSelectors?.getTotalAccessoriesSelectionPrice(state) ?? 0;
    let includedNetTrade = 0;
    if (additionalSelectors?.getIncludedTradeInsNetValue) {
        includedNetTrade = additionalSelectors?.getIncludedTradeInsNetValue?.(state) ?? 0;
    } else {
        includedNetTrade = getIncludedNetTradeValue(state, additionalSelectors);
    }
    const includedIncentives = getIncludedCashIncentives(state);

    const cashOffer =
        offerPrice! +
        totalTax +
        dealerFeesTotal +
        dmvFees +
        totalVppSelectionPrice +
        totalAccessoriesSelectionPrice -
        includedIncentives -
        includedNetTrade;

    return showDecimal ? Number(cashOffer.toFixed(2)) : Math.ceil(cashOffer);
}

export const getTermIdByOfferType = (state: any, offerType?: OfferType): string => {
    const type = offerType || getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, type);
    return selectedOfferDetails.term as string;
};

export const getDownPayment = (
    state: OfferReduxBaseState,
    offerType?: OfferType,
    pencilInputs?: IPencilOfferInputs
): number | undefined => {
    if (pencilInputs == null) {
        const type = offerType || getCurrentOfferType(state);
        const selectedOfferDetails = getOfferDetails(state, type);
        // For Cash deals (when enableCashDownPaymentField is ON), the down payment value is
        // stored at offerDetails.cash.dealDeposit (the cash form input writes there), not at
        // offerDetails.cash.downPayment.
        const isCashDownPaymentFieldEnabled = getFeatureToggle(state, 'enableCashDownPaymentField');
        if (type === CASH && isCashDownPaymentFieldEnabled) {
            return selectedOfferDetails.dealDeposit ?? selectedOfferDetails.downPayment;
        }
        return selectedOfferDetails.downPayment;
    } else {
        return pencilInputs.downPayment;
    }
};

export function getDownPaymentByOfferType(state: OfferReduxBaseState, offerType: OfferType): number | undefined {
    return getDownPayment(state, offerType);
}

export function getSelectedTermByOfferType(state: any, offerType: any): PaymentTerm {
    const selectedTermId = getTermIdForOfferType(state, offerType);
    if (!selectedTermId) {
        return {} as PaymentTerm;
    }
    return getTerm(state, selectedTermId);
}

export function getLenderProgramEndDate(state: OfferReduxBaseState): Date | string {
    const offerType = getCurrentOfferType(state);
    const selectedTerm = getSelectedTermByOfferType(state, offerType);
    return selectedTerm?.lenderProgram?.programEndDate || '';
}
export function getLenderProgramId(state: OfferReduxBaseState): number {
    const offerType = getCurrentOfferType(state);
    const selectedTerm = getSelectedTermByOfferType(state, offerType);
    return selectedTerm?.lenderProgram?.lenderId || 0;
}

export function getInputAnnualMiles(state: any): number {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const annualMiles = selectedOfferDetails.annualMiles;
    return Number(annualMiles);
}

export function getInitialMilesCharged(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const initialMilesCharged = selectedOfferDetails?.initialMilesCharged;
    return initialMilesCharged;
}

export function getInitialMileageCharge(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const initialMileageCharge = selectedOfferDetails?.initialMileageCharge;
    return initialMileageCharge;
}

export function getTotalExcessMilesPurchased(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const totalExcessMilesPurchased = selectedOfferDetails?.totalExcessMilesPurchased;
    return totalExcessMilesPurchased;
}

export function getExcessAmount(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const excessAmount = selectedOfferDetails?.excessAmount;
    return excessAmount;
}

export function getThresholdMiles(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const thresholdMiles = selectedOfferDetails?.thresholdMiles;
    return thresholdMiles;
}

export function getRetroMiles(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const retroMiles = selectedOfferDetails?.retroMiles;
    return retroMiles;
}

export function getRetroMilesThreshold(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const retroMilesThreshold = selectedOfferDetails?.retroMilesThreshold;
    return retroMilesThreshold;
}

export function getInitialMilesThreshold(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const initialMilesThreshold = selectedOfferDetails?.initialMilesThreshold;
    return initialMilesThreshold;
}

export function getStandardMiles(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const standardMiles = selectedOfferDetails?.standardMiles;
    return standardMiles;
}

export function getInceptionRate(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const inceptionRate = selectedOfferDetails?.inceptionRate;
    return inceptionRate;
}

export function getStandardAllowedMiles(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const standardAllowedMiles = selectedOfferDetails?.standardAllowedMiles;
    return standardAllowedMiles;
}

export function getExcessMileRate(state: OfferReduxBaseState): number | undefined {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const excessMileRate = selectedOfferDetails?.excessMileRate;
    return excessMileRate;
}

export function getSecondPaymentDueDate(state: OfferReduxBaseState): string {
    const selectedOfferDetails = getOfferDetails(state, LEASE);
    const secondPaymentDueDate = selectedOfferDetails?.secondPaymentDueDate;
    return secondPaymentDueDate || '';
}

export function getSelectedCreditTierIdByType(state: any, offerType: any): string {
    const offerDetails = getOfferDetails(state, offerType);
    const selectedCreditTierId = offerDetails.creditTier;
    return selectedCreditTierId || '';
}

export function getIncentiveTotalByOfferType(state: OfferReduxBaseState, offerType: OfferType): number | undefined {
    const offerInputs = getOfferDetails(state, offerType);
    return offerInputs.totalIncentives;
}

export function getSelectedIncentivesByOfferType(state: any, offerType: OfferType): string[] {
    const userInput = getOfferDetails(state, offerType);
    if (isEmpty(userInput)) {
        return [];
    }
    return userInput.selectedIncentives ?? [];
}

export const getRateTypeOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType
): paymentServicesTypes.RateType | undefined => {
    const offerDetails = getOfferDetails(state, offerType);
    return offerDetails.rateTypeOverride;
};

// Gets the incentives total amount penciled while credit decision enabled (generic calcs)
export const getIncentivesTotalOverrideByOfferType = (state: OfferReduxBaseState, offerType: OfferType): number | undefined => {
    const { incentiveTotalOverride } = getOfferDetails(state, offerType);
    return incentiveTotalOverride;
};

// TODO Delete getCreditDecisionLenderNameByOfferType after feature toggle enableAppliedLenderInfo has been deleted
export const getCreditDecisionLenderNameByOfferType = (state: OfferReduxBaseState, offerType: OfferType): string | undefined => {
    const inputs = getOfferDetails(state, offerType);
    return inputs.creditDecisionLenderName;
};

export const getCreditDecisionLenderByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType
): ICreditDecisionLender | undefined => {
    const inputs: any = getOfferDetails(state, offerType);
    return inputs.creditDecisionLender;
};

export const getTitlingLenderWithAddressByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType
): TitlingLender | undefined => {
    const inputs = getOfferDetails(state, offerType);
    return inputs.titlingLender;
};

export const getCreditDecisionStatusByOfferType = (state: OfferReduxBaseState, offerType: OfferType): string | undefined => {
    const inputs = getOfferDetails(state, offerType);
    return inputs.creditDecisionStatus;
};

export const getManualIncentivesByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType
): ManualIncentiveEntry[] | undefined => {
    return getOfferDetails(state, offerType)?.manualIncentives?.incentives;
};

export const getManualIncentiveDealerCashByOfferType = (state: OfferReduxBaseState, offerType: OfferType): number => {
    const penciledInputs = getOfferDetails(state, offerType);
    return penciledInputs?.manualIncentives?.dealerCash ?? 0;
};

export const getManualIncentiveRebatesByOfferType = (state: OfferReduxBaseState, offerType: OfferType): number => {
    const penciledInputs = getOfferDetails(state, offerType);
    return penciledInputs?.manualIncentives?.totalRebates ?? 0;
};

export function getDealSummary(state: OfferReduxBaseState): IDealSummary {
    const offerDetails = getOfferDetails(state, getCurrentOfferType(state));
    const term = getTerm(state, offerDetails.term!);
    return term && !isEmpty(term) ? term.dealSummary! : [];
}

export function getMonthlyPayment(state: any, offerType: OfferType = 'finance'): number | null {
    const selectedTerm = getSelectedTermByOfferType(state, offerType);
    return selectedTerm?.monthlyPayment ?? null;
}

export function getMonthlyPaymentByOfferType(state: any, offerType: any): number | null {
    return getMonthlyPayment(state, offerType);
}

export function getMonthlyPaymentRounded(state: any, offerType = OFFER_TYPE.finance): number | null {
    const selectedTerm = getSelectedTermByOfferType(state, offerType);
    return selectedTerm?.monthlyPaymentRounded ?? null;
}

export function getMonthlyPaymentRoundedByOfferType(state: any, offerType: any): number | null {
    return getMonthlyPaymentRounded(state, offerType);
}

export function isMonthlyPaymentInvalid(state: any, offerType?: OfferType): boolean {
    const currentOfferState = offerType || getCurrentOfferType(state);
    return isPaymentInvalid(getMonthlyPayment(state, currentOfferState), currentOfferState);
}

export function getSelectedTerm(state: any, offerType?: OfferType): PaymentTerm {
    const type = offerType ?? getCurrentOfferType(state);
    const selectedTermId = getTermIdForOfferType(state, type);
    if (!selectedTermId) return {} as PaymentTerm;
    return getTerm(state, selectedTermId);
}

export function isSelectedTermRecordMissing(state: any, offerType?: OfferType): boolean {
    const type = offerType ?? getCurrentOfferType(state);
    if (type === CASH) return false;
    const selectedTermId = getTermIdForOfferType(state, type);
    if (!selectedTermId) return true;
    return !getOffer(state)?.terms?.byId?.[selectedTermId];
}

export const getOfferTerm = (state: OfferReduxBaseState, offerType?: OfferType): OfferTermInputs => {
    const type = offerType ?? getCurrentOfferType(state);
    const term = type === CASH ? getOfferDetails(state, CASH) : getSelectedTerm(state, type);
    return term;
};

export const getTaxItemsWithOverrides = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const type = offerType ?? getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, type);
    return offerDetails.taxItems || [];
};

export const getCreditDecisionEnabled = (state: OfferReduxBaseState, offerType?: OfferType): boolean | undefined => {
    const type = offerType ?? getCurrentOfferType(state);
    return getCreditDecisionEnabledByOfferType(state, type);
};

export const getInitialCalculationEndpoint = (state: OfferReduxBaseState): CalculationEndpointType | undefined => {
    return getCalculationEndpointsState(state)?.initialCalculationEndpoint;
};

export const getCalculationEndpointByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType
): CalculationEndpointType | undefined => {
    return getCalculationEndpointsState(state)?.calculationEndpointByOfferType?.[offerType];
};

export const getIsUserProgramQuotes = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const type = offerType ?? getCurrentOfferType(state);
    const calculationEndpoint = getCalculationEndpointByOfferType(state, type);
    return Boolean(calculationEndpoint === CALC_ENDPOINTS.userProgramQuotes);
};

export const getIsUserProgramQuotesSinceLoad = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const loadedOnUserProgramQuotes = getInitialCalculationEndpoint(state) === CALC_ENDPOINTS.userProgramQuotes;
    return loadedOnUserProgramQuotes && getIsUserProgramQuotes(state, offerType);
};

// True when this offer type is priced through /program-quotes, which is what Auto Select being on means.
export const getIsProgramQuotes = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const resolvedOfferType = offerType ?? getCurrentOfferType(state);
    const calculationEndpoint = getCalculationEndpointByOfferType(state, resolvedOfferType);
    // An offer type with no endpoint yet is neither, so this and getIsUserProgramQuotes both return
    // false for it — do not use one as the other's negation.
    return calculationEndpoint === CALC_ENDPOINTS.programQuotes;
};

export function getAdjustedDownPayment(state: OfferReduxBaseState): number {
    if (isMonthlyPaymentInvalid(state)) {
        return 0;
    } else {
        const dealSummary: any = getDealSummary(state);
        const adjustedDownPayment = dealSummary.find((item: any) => {
            return item.key === 'downPayment';
        });
        if (adjustedDownPayment) {
            return adjustedDownPayment.value;
        } else {
            return 0;
        }
    }
}

export function getToggleGridStatus(state: any, offerType: OfferType): boolean {
    const offerDetails = getOfferDetails(state, offerType);
    return offerDetails.paymentGridToggleSP || false;
}

export function getTermsGridList(state: any, offerType: OfferType): number[] {
    const offerDetails = getOfferDetails(state, offerType);
    if (
        offerDetails.gridHeadersData?.terms &&
        offerDetails.gridHeadersData?.terms.length > 0 &&
        offerDetails.gridHeadersData?.terms[0] != null
    )
        return offerDetails.gridHeadersData?.terms as number[];
    return [];
}

export function getDownPaymentsGridList(state: any, offerType: OfferType): number[] {
    const offerDetails = getOfferDetails(state, offerType);
    if (
        offerDetails.gridHeadersData?.downPayments &&
        offerDetails.gridHeadersData?.downPayments.length > 0 &&
        offerDetails.gridHeadersData?.downPayments[0] != null
    )
        return offerDetails.gridHeadersData?.downPayments as number[];
    return [];
}

export function getCashApplyMethod(state: OfferReduxBaseState): string | undefined {
    const offer = getOffer(state);
    return offer.cashApplyMethod;
}

export function getLeaseOfferPrice(state: any, offerType: OfferType): number {
    const offerDetails = getOfferDetails(state, offerType);
    const { vehicleSalePrice, cashOffsetAmountAddedToPrice, offerPrice } = offerDetails;
    if (vehicleSalePrice == undefined || cashOffsetAmountAddedToPrice == undefined) return offerPrice!;
    return vehicleSalePrice - cashOffsetAmountAddedToPrice;
}

export function getVehicleSalePrice(state: any, offerType: OfferType): number {
    // Get the offer type
    const type = offerType ?? getCurrentOfferType(state);

    // Get the selected term
    const selectedTermId = getTermIdForOfferType(state, type);
    const selectedTerm = getTerm(state, selectedTermId);

    // Get the offer details
    const offerDetails = getOfferDetails(state, type);

    const { cashOffsetAmountAddedToPrice } = selectedTerm;
    const { vehicleSalePrice, offerPrice } = offerDetails;

    if (vehicleSalePrice != undefined) return vehicleSalePrice;
    if (offerPrice != undefined && cashOffsetAmountAddedToPrice != undefined) return offerPrice + cashOffsetAmountAddedToPrice;
    return 0;
}

export function getPartialRestoreRecord(state: any, offerType?: OfferType): PartialRestoreRecord {
    // Get the offer type
    const type = offerType ?? getCurrentOfferType(state);

    if (type == CASH) {
        return {
            termMonth: 0,
            calculationEndpoint: CALC_ENDPOINTS.programQuotes,
            record: {}
        };
    }

    // Get the selected term
    const selectedTermId = getTermIdForOfferType(state, type);
    const selectedTerm = getTerm(state, selectedTermId);

    // Get the offer details
    const offerDetails = getOfferDetails(state, type);

    const calculationEndpoints = getCalculationEndpointsState(state);

    return {
        termMonth: selectedTerm.months,
        calculationEndpoint: calculationEndpoints?.currentCalculationEndpoint as CalculationEndpointType,
        record: {
            annualMiles: offerDetails.annualMiles,
            rateTypeOverride: offerDetails.rateTypeOverride,
            sellRateOverride: offerDetails.sellRateOverride,
            buyRateOverride: offerDetails.buyRateOverride,
            securityDepositOverride: offerDetails.securityDepositOverride,
            acqFeeOverride: offerDetails.acqFeeOverride,
            acqFeeUpFrontOverride: offerDetails.acqFeeUpFrontOverride,
            adjustedResidualAmountOverride: offerDetails.adjustedResidualAmountOverride,
            adjustedResidualPercentOverride: offerDetails.adjustedResidualPercentOverride,
            adjustedResidualTypeOverride: offerDetails.adjustedResidualTypeOverride,
            baseResidualAmountOverride: offerDetails.baseResidualAmountOverride,
            baseResidualPercentOverride: offerDetails.baseResidualPercentOverride,
            programResidualPercentOverride: offerDetails.programResidualPercentOverride,
            programResidualOverride: offerDetails.programResidualOverride,
            baseResidualType: offerDetails.baseResidualType,
            overridesByTerm: offerDetails.overridesByTerm,
            feesOverride: offerDetails.feesOverride,
            [OFFER_ENCRYPTION_FIELDS.OVERRIDES]: offerDetails[OFFER_ENCRYPTION_FIELDS.OVERRIDES]
        }
    };
}

export function getNetSellingPrice(state: OfferReduxBaseState): number | undefined {
    const offerDetails = getOfferDetails(state, getCurrentOfferType(state));
    return offerDetails.netSellingPrice;
}

export function getEstTotalPurchasePrice(state: OfferReduxBaseState): number | undefined {
    const offerDetails = getOfferDetails(state, getCurrentOfferType(state));
    return offerDetails.estTotalPurchasePrice;
}

/**
 * @description This selector get the special payment formula from the offer details `offer.penciledInputs.byOfferType.finance.specialPaymentFormula`, this is not the same as the selected term `offer.terms.byId[termId].specialPaymentFormula` because this values is updated when the offer type change
 */
export const isFlexBuyPayment = (state: OfferReduxBaseState): boolean => {
    const offerDetails = getOfferDetails(state, FINANCE);
    return offerDetails?.specialPaymentFormula?.trim().toLowerCase() === SpecialPaymentFormula.FordFlexBuy.trim().toLowerCase();
};

export const getOfferInitialPaymentDiscount = (state: OfferReduxBaseState): number[] => {
    const offerDetails = getOfferDetails(state, FINANCE);
    if ('initialPaymentDiscount' in offerDetails && Array.isArray(offerDetails.initialPaymentDiscount)) {
        return offerDetails.initialPaymentDiscount;
    }

    return [];
};

export const getDealDeposit = (state: OfferReduxBaseState): number => {
    const offerDetails = getOfferDetails(state, CASH);
    return offerDetails?.dealDeposit || 0;
};

export const getOutsideLienholder = (state: OfferReduxBaseState) => {
    const offerDetails = getOfferDetails(state, CASH);
    return offerDetails?.lienHolder || ({} as OutsideLienHolder);
};

export const getTransferRegistrationDetails = (
    state: OfferReduxBaseState
): { registrationType?: string; registrationExpiresOn?: string } => {
    const offerDetails = getOfferDetails(state, getCurrentOfferType(state));
    if (!offerDetails?.registrationType && !offerDetails?.registrationExpiresOn) {
        return {};
    }
    return { registrationType: offerDetails?.registrationType, registrationExpiresOn: offerDetails?.registrationExpiresOn };
};

export const getPriorLeaseBalance = (state: OfferReduxBaseState): PriorLeaseBalance => {
    const penciledInputs = getPenciledInputs(state) as PenciledInputsState;
    return penciledInputs?.priorLeaseBalance || {};
};

const hasPriorLeaseBalanceAmount = (priorLeaseBalance?: PriorLeaseBalance): boolean =>
    typeof priorLeaseBalance?.amount === 'number' && !Number.isNaN(priorLeaseBalance.amount);

export const hasPriorLeaseBalance = (state: OfferReduxBaseState): boolean =>
    hasPriorLeaseBalanceAmount(getPriorLeaseBalance(state));

export const getPriorLeaseBalanceForPayment = (state: OfferReduxBaseState): PriorLeaseBalanceForPayment | undefined => {
    const priorLeaseBalance = getPriorLeaseBalance(state);

    if (!hasPriorLeaseBalanceAmount(priorLeaseBalance)) {
        return undefined;
    }

    const { applyMethod } = priorLeaseBalance;

    return {
        amount: priorLeaseBalance.amount as number,
        ...(applyMethod ? { applyMethod: applyMethod === 'IncreaseSellingPrice' ? 'Capitalized' : applyMethod } : {})
    };
};

/**
 * @description Returns the equity allocation method (string enum) for lease offers only.
 * Empty string ("") means no method selected and should be treated as undefined for payload purposes.
 */
export const getEquityAllocationMethod = (state: OfferReduxBaseState): string | undefined => {
    const currentOfferType = getCurrentOfferType(state);
    if (currentOfferType !== LEASE) return undefined;
    const offerDetails = getOfferDetails(state, currentOfferType);
    const method = offerDetails?.equityAllocationMethod;
    if (typeof method !== 'string' || method === '') return undefined;
    return method;
};

export const getFirstTermPaymentDetail = (state: OfferReduxBaseState): TermPaymentDetail | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.firstTermPaymentDetail;
};

export const getFirstPaymentTerm = (state: OfferReduxBaseState): number | undefined => {
    const firstPaymentTermDetail = getFirstTermPaymentDetail(state);
    return firstPaymentTermDetail?.term;
};

export const getFirstPaymentAmount = (state: OfferReduxBaseState): number | undefined => {
    const firstPaymentTermDetail = getFirstTermPaymentDetail(state);
    return firstPaymentTermDetail?.paymentAmount;
};

export const getSecondTermPaymentDetail = (state: OfferReduxBaseState): TermPaymentDetail | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.secondTermPaymentDetail;
};

export const getCustomerState = (state: OfferReduxBaseState): string | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.state;
};

export const getSecondPaymentTerm = (state: OfferReduxBaseState): number | undefined => {
    const secondPaymentTermDetail = getSecondTermPaymentDetail(state);
    return secondPaymentTermDetail?.term;
};

export const getSecondPaymentAmount = (state: OfferReduxBaseState): number | undefined => {
    const secondPaymentTermDetail = getSecondTermPaymentDetail(state);
    return secondPaymentTermDetail?.paymentAmount;
};

export const comparableTermPaymentDetail = (state: OfferReduxBaseState) => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.comparableTermPaymentDetail;
};

export const comparableTermMonths = (state: OfferReduxBaseState): number | undefined => {
    const comparableTerm = comparableTermPaymentDetail(state);
    return comparableTerm?.term;
};

export const comparableTermPaymentAmount = (state: OfferReduxBaseState): number | undefined => {
    const comparableTerm = comparableTermPaymentDetail(state);
    return comparableTerm?.paymentAmount;
};

export const getEnctyptedPaymentByOfferType = (state: any, offerType?: OfferType): string => {
    const offerDetails = getOfferDetails(state, offerType ?? getCurrentOfferType(state));
    return offerDetails[OFFER_ENCRYPTION_FIELDS.PAYMENT] || '';
};

export const getEncryptedIncentivesByOfferType = (state: any, offerType?: OfferType): string => {
    const offerDetails = getOfferDetails(state, offerType ?? getCurrentOfferType(state));
    return offerDetails[OFFER_ENCRYPTION_FIELDS.INCENTIVES] || '';
};

export const getEnctyptedOverridesByOfferType = (state: any, offerType?: OfferType): string => {
    const offerDetails = getOfferDetails(state, offerType ?? getCurrentOfferType(state));
    return offerDetails[OFFER_ENCRYPTION_FIELDS.OVERRIDES] || '';
};

export const getEncryptedVehicleInvoiceAmount = (state: any): string => {
    const vehicle = getVehicleDetails(state) as Record<string, string | undefined>;
    return vehicle?.[VEHICLE_ENCRYPTION_FIELDS.INVOICE_AMOUNT] || '';
};

export const getEncryptedVehicleCost = (state: any): string => {
    const vehicle = getVehicleDetails(state) as Record<string, string | undefined>;
    return vehicle?.[VEHICLE_ENCRYPTION_FIELDS.COST] || '';
};

export const getHonorOverrides = (state: any, offerType?: OfferType): boolean | undefined => {
    const offerDetails = getOfferDetails(state, offerType ?? getCurrentOfferType(state));
    return offerDetails.honorOverrides;
};

export const getIncludedCashIncentives = createSelector([getCashOfferDetails], (cashOfferDetails) => {
    const { totalIncentives, appliedIncentiveAmount } = cashOfferDetails;
    return appliedIncentiveAmount || totalIncentives || 0;
});

export const getPreviousFinanceInputWithoutFlexBuy = (
    state: OfferReduxBaseState
): PenciledInputsState['previousFinanceInputWithoutFlexBuy'] => {
    const penciledInputs = getPenciledInputs(state) as PenciledInputsState;
    return penciledInputs?.previousFinanceInputWithoutFlexBuy;
};

export const getOfferDealerDiscounts = (state: OfferReduxBaseState): ItemizedDiscount[] => {
    const penciledInputs = getPenciledInputs(state) as PenciledInputsState;
    return getDealerDiscounts(penciledInputs);
};

export const getOfferDealerDiscountsTotalAmount = (state: OfferReduxBaseState): number => {
    const penciledInputs = getPenciledInputs(state) as PenciledInputsState;
    return getDealerDiscountsTotalAmount(penciledInputs);
};

export const getCapitalizeFirstPayment = (state: OfferReduxBaseState, offerType?: OfferType): boolean | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, type);
    return selectedOfferDetails.capitalizeFirstPayment;
};

export const getIsTargetOutOfPocket = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const type = offerType || getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, type);
    return selectedOfferDetails.isTargetOutOfPocket || false;
};

export const getOfferTermId = (state: OfferReduxBaseState, offerType?: OfferType): string => {
    const penciledInputs = getPenciledInputs(state) as PenciledInputsState;
    const type = offerType || getCurrentOfferType(state);
    return getOfferTypeTermId(penciledInputs, type)!;
};

export const getOfferPaymentFrequency = (state: OfferReduxBaseState, offerType?: OfferType): PaymentSchedule | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const penciledInputs = getOfferDetails(state, type);
    return getInputPaymentFrequency(penciledInputs);
};

export const isOfferOnePayLease = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const type = offerType || getCurrentOfferType(state);
    const penciledInputs = getPenciledInputs(state) as PenciledInputsState;
    const paymentFrequency = getInputPaymentFrequency(penciledInputs?.byOfferType?.[type] || {});
    return isOnePayLeaseFrequency(paymentFrequency);
};

export const getInitialPaymentDiscount = (state: OfferReduxBaseState, term?: PaymentTerm): number | undefined => {
    const selectedTerm = term == null ? getSelectedTerm(state) : term;
    return getTermInitialPaymentDiscount(selectedTerm);
};

export const getTermInitialPaymentDiscount = (term: PaymentTerm): number | undefined => {
    return term?.initialPaymentDiscount;
};

export const getDeferredDownPayment = (state: OfferReduxBaseState): DeferredDownPayment | undefined => {
    const offerType = getCurrentOfferType(state);
    const deferredDownPayment = offerType === FINANCE ? getOfferDetails(state, offerType)?.deferredDownPayment : undefined;
    return deferredDownPayment;
};
